// The coach chat's daily message limit (Graphite, 2026-10-30): the header counter, the "approaching" row above the
// input (1–2 left) and the limit-reached card that takes the input's place. UI only — the limit, the count and the
// reset are the server's (/usage, /chat); the countdown runs to the server's reset time (utils/dailyQuota.js).
import React, { useEffect, useRef, useState } from 'react';
import { Pause } from '@phosphor-icons/react';
import { resetTime, countdownText, resetLabel } from '../../utils/dailyQuota';

/** "8/10 הודעות היום" — the number turns bright and bold when 2 or fewer are left. */
export function QuotaCounter({ used, limit }) {
  const near = limit > 0 && limit - used <= 2;
  return (
    <span className="text-[13px] text-text-muted tabular-nums" data-testid="header-quota"
          aria-label={`${used} מתוך ${limit} הודעות היום`}>
      <span className={near ? 'text-text font-bold' : ''} dir="ltr">{used}/{limit}</span> הודעות היום
    </span>
  );
}

/** 1–2 messages left: the line and a meter (one pill per message; lime = left). The text carries the meaning. */
export function QuotaApproaching({ remaining, limit }) {
  const pills = Array.from({ length: Math.max(0, limit) }, (_, i) => i < remaining);
  return (
    <div className="flex items-center justify-between gap-3 pb-2" data-testid="quota-approaching">
      <span className="text-[13px] font-bold text-text">
        {remaining === 1 ? 'נשארה הודעה אחת להיום' : `נשארו ${remaining} הודעות להיום`}
      </span>
      <span className="flex flex-1 min-w-0 justify-start gap-[3px]" dir="ltr" aria-hidden="true" data-testid="quota-meter">
        {pills.map((left, i) => (
          <span key={i} data-testid="quota-pill" data-left={left ? 'true' : 'false'}
                className={`block h-1 rounded-[2px] ${left ? 'bg-accent' : 'bg-[var(--g-bar)]'}`}
                style={{ width: 14, flexShrink: 1, minWidth: 2 }} />
        ))}
      </span>
    </div>
  );
}

/** The limit-reached card (replaces the input area). onReset fires once when the countdown reaches 00:00:00;
 *  onUrgent (optional) opens one message for something urgent — the server still decides (its emergency check runs
 *  before the limit; anything else gets the limit reply, no model, not counted). */
export function DailyLimitCard({ limit, trialDaysLeft, onTrial, resetsAt, onReset, onUrgent }) {
  const [now, setNow] = useState(Date.now());
  const [target, setTarget] = useState(() => resetTime(resetsAt));      // fixed once: never re-derived from `now`
  const fired = useRef(false);
  useEffect(() => {
    setTarget(resetTime(resetsAt));
    fired.current = false;
  }, [resetsAt]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (now >= target && !fired.current) {
      fired.current = true;
      if (onReset) onReset();
    }
  }, [now, target, onReset]);
  const showTrial = onTrial && trialDaysLeft > 0;
  return (
    <div className="mx-3 mb-3 rounded-[20px] bg-surface border border-border px-4 py-[18px]" dir="rtl"
         role="status" aria-live="polite" data-testid="daily-limit-card">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 shrink-0 rounded-[12px] bg-bg border border-border flex items-center justify-center text-text-body">
          <Pause size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="m-0 text-[18px] font-extrabold leading-snug text-text">הגעת למכסת ההודעות היומית</p>
          <p className="m-0 mt-0.5 text-[14px] text-text-muted">השתמשת ב-{limit} הודעות היום.</p>
        </div>
      </div>

      <div className={`mt-4 grid ${showTrial ? 'grid-cols-2' : 'grid-cols-1'} border-y border-border`}>
        <div className="py-3">
          <p className="m-0 num-display text-[36px] leading-none text-text">{limit}</p>
          <p className="m-0 mt-1 text-[13px] text-text-muted">הודעות מחר</p>
        </div>
        {showTrial && (
          <div className="py-3 ps-4 border-s border-border" data-testid="limit-trial">
            <p className="m-0 num-display text-[36px] leading-none text-text">{trialDaysLeft}</p>
            <p className="m-0 mt-1 text-[13px] text-text-muted">ימי ניסיון נותרו</p>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <div>
          <p className="m-0 text-[13px] text-text-muted">המכסה תתחדש בעוד</p>
          <p className="m-0 text-[12px] text-text-muted" data-testid="limit-reset-label">{resetLabel(target)}</p>
        </div>
        <p className="m-0 num-display text-[44px] leading-none text-accent tabular-nums" dir="ltr"
           data-testid="limit-countdown">{countdownText(target, now)}</p>
      </div>

      <p className="m-0 mt-3 text-[13px] text-text-body">אפשר להמשיך לקרוא את השיחה ולאשר הצעות לשינוי בתכנית.</p>
      {onUrgent && (
        <div className="mt-2" data-testid="limit-urgent">
          <button type="button" onClick={onUrgent} data-testid="limit-urgent-open"
                  className="p-0 bg-transparent border-none text-[13px] text-text-body underline underline-offset-2 cursor-pointer">
            יש חשש לפציעה או בעיה רפואית?
          </button>
          <p className="m-0 mt-0.5 text-[12px] text-text-muted">במקרה חירום רפואי יש לפנות מיד למוקד 101.</p>
        </div>
      )}
    </div>
  );
}
